import { useRef, useCallback, useEffect, useState } from "react";

export default function useAutoScroll({ speed = 2 } = {}) {
  const RESUME_DELAY = 4000; // задержка для возобновления авто‑прокрутки
  const PROGRAMMATIC_SCROLL_THRESHOLD = 150; // порог для определения программного скролла (мс)

  // Ссылка на контейнер скролла
  const containerRef = useRef(null);
  // Текущее направление: 1 – движение вправо, -1 – движение влево
  const [scrollDirection, setScrollDirection] = useState(1);
  // Флаг взаимодействия пользователя с контейнером
  const [isUserInteracting, setIsUserInteracting] = useState(false);

  const autoScrollInterval = useRef(null);
  const resumeTimerRef = useRef(null);
  const lastProgrammaticScrollTime = useRef(0);

  // Остановка интервала автоскролла
  const stopAutoScroll = useCallback(() => {
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
      autoScrollInterval.current = null;
    }
  }, []);

  // Запуск автоскролла (если пользователь не взаимодействует)
  const startAutoScroll = useCallback(() => {
    if (isUserInteracting) return;
    stopAutoScroll();

    autoScrollInterval.current = setInterval(() => {
      const container = containerRef.current;
      if (!container) return;

      // Фиксируем время программного скролла
      lastProgrammaticScrollTime.current = Date.now();

      if (scrollDirection === 1) {
        const maxScrollLeft = container.scrollWidth - container.offsetWidth;
        const newScrollLeft = container.scrollLeft + speed;
        if (newScrollLeft >= maxScrollLeft) {
          container.scrollLeft = maxScrollLeft;
          stopAutoScroll();
          if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
          resumeTimerRef.current = setTimeout(() => {
            // Если авто‑скролл дошёл до правого края, то обычная логика – смена направления на -1.
            setScrollDirection(-1);
            startAutoScroll();
            resumeTimerRef.current = null;
          }, RESUME_DELAY);
        } else {
          container.scrollLeft = newScrollLeft;
        }
      } else {
        // scrollDirection === -1
        const newScrollLeft = container.scrollLeft - speed;
        if (newScrollLeft <= 0) {
          container.scrollLeft = 0;
          stopAutoScroll();
          if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
          resumeTimerRef.current = setTimeout(() => {
            // Если авто‑скролл дошёл до левого края, то меняем направление на 1.
            setScrollDirection(1);
            startAutoScroll();
            resumeTimerRef.current = null;
          }, RESUME_DELAY);
        } else {
          container.scrollLeft = newScrollLeft;
        }
      }
    }, 20);
  }, [scrollDirection, speed, isUserInteracting, stopAutoScroll]);

  // При завершении взаимодействия (mouseLeave/touchEnd) запускаем восстановление автоскролла.
  // Здесь мы проверяем положение контейнера:
  // • Если scrollDirection === 1 (движемся вправо, стартовая точка = левый край), и контейнер в итоге на левом крае,
  //   значит пользователь «оттянул» его к стартовой точке – оставляем направление 1.
  // • Если же контейнер оказался на правом крае, то меняем направление на -1.
  // Аналогично для scrollDirection === -1.
  const resetResumeTimer = useCallback(() => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      setIsUserInteracting(false);
      const container = containerRef.current;
      if (container) {
        const maxScrollLeft = container.scrollWidth - container.offsetWidth;
        if (scrollDirection === 1) {
          // Для движения вправо стартовая точка – левый край (scrollLeft ≈ 0)
          if (container.scrollLeft <= 0) {
            // Если пользователь вернул скролл к левому краю – остаёмся в направлении 1
            setScrollDirection(1);
          } else if (container.scrollLeft >= maxScrollLeft) {
            // Если же скролл оказался у правого края – меняем направление на -1
            setScrollDirection(-1);
          }
        } else if (scrollDirection === -1) {
          // Для движения влево стартовая точка – правый край
          if (container.scrollLeft >= maxScrollLeft) {
            setScrollDirection(-1);
          } else if (container.scrollLeft <= 0) {
            setScrollDirection(1);
          }
        }
      }
      startAutoScroll();
      resumeTimerRef.current = null;
    }, RESUME_DELAY);
  }, [startAutoScroll, scrollDirection]);

  // При начале взаимодействия – останавливаем автоскролл и сбрасываем таймер восстановления
  const handleUserInteractionStart = useCallback(() => {
    setIsUserInteracting(true);
    stopAutoScroll();
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, [stopAutoScroll]);

  // При окончании взаимодействия – запускаем восстановление автоскролла (с проверкой положения)
  const handleUserInteractionEnd = useCallback(() => {
    resetResumeTimer();
  }, [resetResumeTimer]);

  // Монтирование: запускаем автоскролл; размонтирование: очищаем интервалы и таймеры
  useEffect(() => {
    startAutoScroll();
    return () => {
      stopAutoScroll();
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, [startAutoScroll, stopAutoScroll]);

  // При изменении размеров окна сбрасываем таймеры, флаг взаимодействия и перезапускаем автоскролл
  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (resumeTimerRef.current) {
          clearTimeout(resumeTimerRef.current);
          resumeTimerRef.current = null;
        }
        stopAutoScroll();
        setIsUserInteracting(false);
        const container = containerRef.current;
        if (container) {
          const maxScrollLeft = container.scrollWidth - container.offsetWidth;
          if (container.scrollLeft >= maxScrollLeft) {
            setScrollDirection(-1);
          } else if (container.scrollLeft <= 0) {
            setScrollDirection(1);
          }
        }
        startAutoScroll();
      }, 200);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [startAutoScroll, stopAutoScroll]);

  // Если происходит событие scroll (не программное), считаем, что это действие пользователя –
  // останавливаем автоскролл и запускаем восстановление
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onScroll = () => {
      const now = Date.now();
      if (now - lastProgrammaticScrollTime.current < PROGRAMMATIC_SCROLL_THRESHOLD)
        return;
      handleUserInteractionStart();
      resetResumeTimer();
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
    };
  }, [handleUserInteractionStart, resetResumeTimer]);

  return {
    containerRef,
    handleMouseEnter: handleUserInteractionStart,
    handleMouseLeave: handleUserInteractionEnd,
    handleTouchStart: handleUserInteractionStart,
    handleTouchEnd: handleUserInteractionEnd,
  };
}
